<template>
  <div v-if="applicant != null">

    <div class="flex items-center">
      <PageHeader :label="getApplicantName(applicant)"></PageHeader>
      <a href="#" 
        v-if="$store.state.view == 'broker'"
        class="bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-2 rounded-sm text-xs ml-8"
        @click.prevent="showSelectDocumentTypeModal">Select document types
      </a>
    </div>

    <div class="my-8">
      <template v-if="documents == null"></template>

      <template v-else-if="documents != null && documents.length == 0">
        <div class="text-gray-700 text-base bg-gray-200 p-6 rounded border border-gray-300 text-center">

          <img src="https://cdn0.iconfinder.com/data/icons/25-essential-ui-pack-1/32/Delete-512.png" 
            class="w-12 inline" />

          <div class="mt-4">
            You have not added any document types. 
            <a href="#" @click.prevent="showSelectDocumentTypeModal" class="underline">Let's add one now</a>.
          </div>

          <template v-if="templates != null && templates.length > 0 && $store.state.view == 'broker'">
            <div class="mt-2 text-base">
              You can also add documents via templates that you have created.
            </div>

            <div class="mt-2">
              <select v-on:change="templateSelected" class="border border-gray-500">
                <option>Select a template</option>
                <template v-for="(template, index) in templates">
                  <option :value="template.id">{{ template.name }}</option>
                </template>
              </select>
            </div>
          </template>
        </div>
      </template>

      <template v-else>
        <template v-for="(category, categoryIndex) in categories">
          
          <div :class="{ 'text-xl font-light p-2 bg-gray-200' : true, 'mt-8' : categoryIndex > 0 }">{{ category.name }}</div>

          <template v-if="category.subcategories != null">
            <template v-for="(subcategory, subcategoryIndex) in category.subcategories">

              <div class="text-base text-gray-700 mt-6">{{ subcategory.name }}</div>

              <template v-for="(subcategoryDocument, subcategoryDocumentIndex) in subcategory.documents">
                <template v-for="(document, documentIndex) in documents">
                  <template v-if="document.id == subcategoryDocument.id">
                    <div class="border border-gray-500 mt-6">
                      <div class="bg-gray-100 border-b border-gray-500 p-3 flex items-center justify-between">
                        <div class="text-sm flex items-center">

                          <template v-if="document.method == 'upload' && document.files.length > 0">
                            <img src="https://cdn4.iconfinder.com/data/icons/gradient-ui-1/512/success-128.png" class="w-4 mr-4" />
                          </template>

                          {{ document.type }}

                          <a :href="document.example_url"
                            target="_blank"
                            v-if="document.example_url != null"
                            class="ml-4 text-blue-500 hover:text-blue-600 hover:underline">Example</a>

                          <img src="https://cdn2.iconfinder.com/data/icons/officeicons/PNG/48/Help.png" 
                            class="w-4 inline ml-4"
                            v-if="document.tooltip != null && document.tooltip.length > 0" 
                            v-tooltip.top="{ content: document.tooltip }" />
                        </div>

                        <div class="flex items-center">
                          
                          <select class="border border-gray-500 outline-none" 
                            :value="document.method"
                            v-on:change="updateDocumentMethod($event.target.value, documentIndex)">
                            <option value="upload">Upload</option>
                            <option value="faxed">Faxed</option>
                            <option value="posted">Posted</option>
                            <option value="in-person">In Person</option>
                            <option value="emailed">Emailed</option>
                            <option value="illion-bankstatements">Bankstatements via illion</option>
                          </select>

                          <a href="#"
                            v-if="$store.state.view == 'broker'"
                            @click.prevent="deleteApplicaltDocument(documentIndex)"
                            class="text-gray-500 hover:text-gray-700 ml-6">
                            <svg width="20" height="20" stroke-width="2" stroke="currentColor" fill="none">
                              <use xlink:href="/static/img/feather-sprite.svg#x-circle"/>
                            </svg>
                          </a>
                        </div>
                      </div>

                      <div class="">
                        <template v-if="document.method == 'upload'">
                          <template v-for="(file, fileIndex) in document.files">
                            <div :class="{ 'p-3' : true, 'border-t' : fileIndex > 0 }">
                              <div class="flex items-center">

                                <div class="text-gray-700 mr-2">
                                  {{ fileIndex + 1 }}.
                                </div>

                                <a :href="getFileDownloadUrl(file.id)" 
                                  class="text-blue-600 hover:underline mr-4"
                                  target="_blank">{{ file.file_name }}.{{ file.extension }}</a>

                                <a href="#" 
                                  v-if="file.extension.toLowerCase() == 'jpg' || file.extension.toLowerCase() == 'jpeg'"
                                  @click.prevent="convertJpgToPdf(file.id, documentIndex, fileIndex)" 
                                  class="text-gray-700 hover:underline hover:text-red-500 text-xs"
                                  target="_blank">Convert to PDF</a>

                                <span class="mx-1 text-gray-400">/</span>

                                <a href="#" 
                                  v-if="getEnv != 'production'"
                                  @click.prevent="renameFile(file.id, documentIndex, fileIndex)" 
                                  class="text-gray-700 hover:underline hover:text-red-500 text-xx"
                                  target="_blank">Rename</a>

                                <span class="mx-1 text-gray-400">/</span>

                                <a href="#" 
                                  @click.prevent="deleteFile(file.id, documentIndex, fileIndex)" 
                                  class="text-gray-700 hover:underline hover:text-red-500 text-xs"
                                  target="_blank">Delete</a>
                              </div>

                              <template v-if="document.question != null">
                                <div class="ml-5 mt-2">
                                  {{ document.question }}

                                  <div class="mt-1">
                                    <input type="text" class="p-2 border border-gray-500 outline-none"
                                      style="width:400px;" 
                                      :value="file.applicant_document_answer"
                                      v-on:blur="fileAnswerChanged($event.target.value, documentIndex, fileIndex)" />
                                  </div>
                                </div>
                              </template>
                            </div>
                          </template>

                          <div :class="{ 'p-3 flex items-center' : true,  'border-t' : document.files.length > 0 }">
                            <input type="file" multiple v-on:change="upload($event, document.id, documentIndex)" />
                          </div>
                        </template>

                        <template v-else>
                          <div class="text-sm text-gray-700 p-4">

                            <template v-if="document.method == 'illion-bankstatements'">
                              <div class="">
                                Send your bank statements to your broker electronically via a secure portal - Only you can access your accounts in the portal. Your broker will not have access to your bank accounts
                              </div>

                              <div class="mt-4" v-if="$store.state.view == 'client'">
                                <a href="#"
                                   class="inline-block bg-blue-500 hover:bg-green-600 text-white font-semibold px-4 py-2 rounded-sm"
                                   @click.prevent="showBankstatementIntegrationModal">Access secure portal</a>
                              </div>
                            </template>

                            <template v-else>
                              You have marked this document as
                            
                              <template v-if="document.method == 'faxed'">
                                <span class="font-semibold">faxed</span>.
                              </template>

                              <template v-if="document.method == 'posted'">
                                <span class="font-semibold">posted</span>.
                              </template>

                              <template v-if="document.method == 'in-person'">
                                <span class="font-semibold">given in person</span>.
                              </template>

                              <template v-if="document.method == 'emailed'">
                                <span class="font-semibold">emailed</span>.
                              </template>
                            </template>

                          </div>
                        </template>
                      </div>
                    </div>
                  </template>
                </template>
              </template>
            </template>
          </template>

          <template v-else>
            <template v-for="(categoryDocument, categoryDocumentIndex) in category.documents">
              <template v-for="(document, documentIndex) in documents">
                <template v-if="document.id == categoryDocument.id">
                  <div class="border border-gray-500 mt-6">
                    <div class="bg-gray-100 border-b border-gray-500 p-3 flex items-center justify-between">
                      <div class="text-sm flex items-center">

                        <template v-if="document.method == 'upload' && document.files.length > 0">
                          <img src="https://cdn4.iconfinder.com/data/icons/gradient-ui-1/512/success-128.png" class="w-4 mr-4" />
                        </template>

                        {{ document.type }}

                        <a :href="document.example_url"
                          target="_blank"
                          v-if="document.example_url != null"
                          class="ml-4 text-blue-500 hover:text-blue-600 hover:underline">Example</a>

                        <img src="https://cdn2.iconfinder.com/data/icons/officeicons/PNG/48/Help.png" 
                          class="w-4 inline ml-4"
                          v-if="document.tooltip != null" 
                          v-tooltip.top="{ content: document.tooltip }" />
                      </div>

                      <div class="flex items-center">
                        
                        <select class="border border-gray-500 outline-none" 
                          :value="document.method"
                          v-on:change="updateDocumentMethod($event.target.value, documentIndex)">
                          <option value="upload">Upload</option>
                          <option value="faxed">Faxed</option>
                          <option value="posted">Posted</option>
                          <option value="in-person">In Person</option>
                          <option value="emailed">Emailed</option>
                          <option value="illion-bankstatements">Bankstatements via illion</option>
                        </select>

                        <a href="#"
                          v-if="$store.state.view == 'broker'"
                          @click.prevent="deleteApplicaltDocument(documentIndex)"
                          class="text-gray-500 hover:text-gray-700 ml-6">
                          <svg width="20" height="20" stroke-width="2" stroke="currentColor" fill="none">
                            <use xlink:href="/static/img/feather-sprite.svg#x-circle"/>
                          </svg>
                        </a>
                      </div>
                    </div>

                    <div class="">
                      <template v-if="document.method == 'upload'">
                        <template v-for="(file, fileIndex) in document.files">
                          <div :class="{ 'p-3' : true, 'border-t' : fileIndex > 0 }">
                            <div class="flex items-center">

                              <div class="text-gray-700 mr-2">
                                {{ fileIndex + 1 }}.
                              </div>

                              <a :href="getFileDownloadUrl(file.id)" 
                                class="text-blue-600 hover:underline mr-4"
                                target="_blank">{{ file.file_name }}.{{ file.extension }}</a>

                              <a href="#" 
                                v-if="file.extension.toLowerCase() == 'jpg' || file.extension.toLowerCase() == 'jpeg'"
                                @click.prevent="convertJpgToPdf(file.id, documentIndex, fileIndex)" 
                                class="text-gray-700 hover:underline hover:text-red-500 text-xs"
                                target="_blank">Convert to PDF</a>

                              <span class="mx-1 text-gray-400">/</span>

                              <a href="#" 
                                  v-if="getEnv != 'production'"
                                  @click.prevent="renameFile(file.id, documentIndex, fileIndex)" 
                                  class="text-gray-700 hover:underline hover:text-red-500 text-xs"
                                  target="_blank">Rename</a>

                              <span class="mx-1 text-gray-400">/</span>

                              <a href="#" 
                                @click.prevent="deleteFile(file.id, documentIndex, fileIndex)" 
                                class="text-gray-700 hover:underline hover:text-red-500 text-xs"
                                target="_blank">Delete</a>
                            </div>

                            <template v-if="document.question != null">
                              <div class="ml-5 mt-2">
                                {{ document.question }}

                                <div class="mt-1">
                                  <input type="text" class="p-2 border border-gray-500 outline-none"
                                    style="width:400px;" 
                                    :value="file.applicant_document_answer"
                                    v-on:blur="fileAnswerChanged($event.target.value, documentIndex, fileIndex)" />
                                </div>
                              </div>
                            </template>
                          </div>
                        </template>

                        <div :class="{ 'p-3 flex items-center' : true,  'border-t' : document.files.length > 0 }">
                          <input type="file" multiple v-on:change="upload($event, document.id, documentIndex)"/>
                        </div>
                      </template>

                      <template v-else>
                        <div class="text-sm text-gray-900 p-4">
                          
                          <template v-if="document.method == 'illion-bankstatements'">
                            <div class="">
                              Send your bank statements to your broker electronically via a secure portal - Only you can access your accounts in the portal. Your broker will not have access to your bank accounts
                            </div>

                            <div class="mt-4" v-if="$store.state.view == 'client'">
                              <a href="#"
                                 class="inline-block bg-blue-500 hover:bg-green-600 text-white font-semibold px-4 py-2 rounded-sm"
                                 @click.prevent="showBankstatementIntegrationModal">Access secure portal</a>
                            </div>
                          </template>

                          <template v-else>
                            You have marked this document as
                          
                            <template v-if="document.method == 'faxed'">
                              <span class="font-semibold">faxed</span>.
                            </template>

                            <template v-if="document.method == 'posted'">
                              <span class="font-semibold">posted</span>.
                            </template>

                            <template v-if="document.method == 'in-person'">
                              <span class="font-semibold">given in person</span>.
                            </template>

                            <template v-if="document.method == 'emailed'">
                              <span class="font-semibold">emailed</span>.
                            </template>
                          </template>

                        </div>
                      </template>
                    </div>
                  </div>
                </template>
              </template>
            </template>
          </template>
        </template>
      </template>
    </div>

    <div class="button-line">
      <ApplicationButton />
    </div>

    <SelectDocumentTypeModal />
    <IllionBankstatementsModal />
    <FileErrorModal />
    <RenameModal />
  </div>
</template>

<script>
  import axios                      from 'axios'
  import PageHeader                 from '@/shared/components/headers/PageHeader'
  import ApplicationButton          from '@/shared/components/form/ApplicationButton'
  import SelectDocumentTypeModal    from './SelectDocumentTypeModal'
  import IllionBankstatementsModal  from "./IllionBankstatementsModal";
  import { EventBus }               from '@/shared/event-bus'
  import FileErrorModal             from './FileErrorModal'
  import RenameModal                from './RenameModal'

  export default {
    components : {
      PageHeader,
      ApplicationButton,
      SelectDocumentTypeModal,
      FileErrorModal,
      IllionBankstatementsModal,
      RenameModal
    },
    
    data () {
      return {
        applicant : null,
        documents : null,
        categories: null,
        templates : null,
      }
    },

    watch: {
      '$route' (to, from) {
        this.prepare()
      }
    },

    created() {
      this.logActivity({
        type        : 'document-visited',
        clientId    : this.$route.params.clientId,
        currentView : this.$store.state.view
      })

      this.prepare()

      if(this.$store.state.view == 'broker') {
        this.getTemplates()
      }
    },

    mounted() {
      EventBus.$on('get-applicant-documents', () => {
        this.getApplicantDocuments()
      })
    },

    beforeDestroy() {
      EventBus.$off('get-applicant-documents')
    },

    methods : {

      getTemplates() {
        this.apiGet({
          type : "document"
        }, 'template/list', (templates) => {
          this.templates = templates       
        })
      },

      templateSelected(e) {
        if(e.target.value != null) {
          this.showLoader("Applying template...")
          this.apiPost({
            clientId   : this.$route.params.clientId,
            applicantId: this.$route.params.applicantId,
            templateId : e.target.value
          }, 'document/apply-template', (assets) => {
            this.hideLoader()
            this.getApplicantDocuments()
          })
        }
      },

      getFileDownloadUrl(fileId) {
        return this.getBackendHost +'/api/document/download-file/' + fileId + "?clientId=" + this.$route.params.clientId + "&token=" + localStorage.getItem('bp-broker-jwt')
      },

      showSelectDocumentTypeModal() {
        this.$modal.show('select-document-type-modal', {
          documents : this.documents
        })
      },

      showBankstatementIntegrationModal() {
        this.$modal.show('illion-bankstatements-modal', {
          broker : this.$store.state[this.$store.state.view + 'Module'].broker
        })
      },

      prepare() {
        this.getApplicant({
          clientId   : this.$route.params.clientId,
          applicantId: this.$route.params.applicantId
        }, (applicant) => {
          this.applicant = applicant
        })

        this.getApplicantDocuments()
      },

      getApplicantDocuments() {
        this.apiGet({
          clientId   : this.$route.params.clientId,
          applicantId: this.$route.params.applicantId
        }, 'document/applicant-documents', (res) => {
          this.categories = res.categories
          this.documents  = res.documents
        })
      },

      updateDocumentMethod(value, documentIndex) {
        this.documents[documentIndex].method = value
        EventBus.$emit('set-autosave', true)
        this.apiPost({
          clientId   : this.$route.params.clientId,
          applicantId: this.$route.params.applicantId,
          documentId : this.documents[documentIndex].id,
          method     : value
        }, 'document/applicant-document-method', () => {
          EventBus.$emit('set-autosave', false)
        })
      },

      fileAnswerChanged(value, documentIndex, fileIndex) {
        this.documents[documentIndex].files[fileIndex].applicant_document_answer = value
        EventBus.$emit('set-autosave', true)
        this.apiPost({
          clientId   : this.$route.params.clientId,
          applicantId: this.$route.params.applicantId,
          documentId : this.documents[documentIndex].id,
          fileId     : this.documents[documentIndex].files[fileIndex].id,
          answer     : value
        }, 'document/applicant-document-answer', () => {
          EventBus.$emit('set-autosave', false)
        })
      },

      upload($event, documentId, documentIndex) {
        let formData = new FormData()

        // get all the files
        let files = $event.target.files || $event.dataTransfer.files
        for(let i=0; i < files.length; i++) {
          formData.append('files[]', files[i])
        }
        
        formData.append('clientId', this.$route.params.clientId)
        formData.append('applicantId', this.$route.params.applicantId)
        formData.append('fileType', 'document')
        formData.append('documentId', documentId)

        this.showLoader("Uploading document...")
        axios.post(process.env.VUE_APP_API_URL + '/api/document/document-file', formData, {
          headers: {
            'Content-Type'  : 'multipart/form-data',
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        }).then(res => {
          this.hideLoader()

          if(res.data.error != null) {
            this.$modal.show('file-error-modal')
          }

          else {
            for(let i=0; i < res.data.length; i++) {
              this.documents[documentIndex].files.push(res.data[i])
            }
          }
        })
        .catch(e => {
          this.hideLoader()
          this.$modal.show('file-error-modal')
        });
      },

      deleteApplicaltDocument(documentIndex) {
        this.$modal.show('confirm-modal', {
          // width : 600,
          title  : "Please confirm your action",
          message: "Are you sure you want to delete this?",
          buttons:[{
            label          : 'Yes',
            color          : 'red',
            loadingViaLocal: true, // a bit of HACK, we want to show loading image when button is clicked, but hard to pass loading prop to a modal
            action         : () => {
              this.apiDelete({
                clientId   : this.$store.state[this.$store.state.view + 'Module'].client.id,
                applicantId: this.$route.params.applicantId,
                documentId : this.documents[documentIndex].id
              }, 'document/applicant-document', () => {
                this.documents.splice(documentIndex, 1)
                EventBus.$emit('notification-event', {
                  'type'   : 'success',
                  'message': "Document deleted successfully"
                })
                this.$modal.hide('confirm-modal')
              })
            }
          }, {
            label : 'No',
            color : 'gray',
            action: () => {
              this.$modal.hide('confirm-modal')
            }
          }]
        })
      },

      convertJpgToPdf(fileId, documentIndex, fileIndex) {
        this.$modal.show('confirm-modal', {
          // width : 600,
          title  : "Please confirm your action",
          message: "Are you sure you want to convert to PDF?",
          buttons:[{
            label          : 'Yes',
            color          : 'blue',
            loadingViaLocal: true, // a bit of HACK, we want to show loading image when button is clicked, but hard to pass loading prop to a modal
            action         : () => {
              this.apiPost({
                clientId: this.$route.params.clientId,
                fileId  : fileId
              }, 'document/convert-jpg-to-pdf', (file) => {

                if(file.error == null) {
                  this.$modal.hide('confirm-modal')
                  this.documents[documentIndex].files[fileIndex].extension = 'pdf'
                  EventBus.$emit('notification-event', {
                    'type'   : 'success',
                    'message': "File converted successfully"
                  })
                }

                else {

                }
              })
            }
          }, {
            label : 'No',
            color : 'gray',
            action: () => {
              this.$modal.hide('confirm-modal')
            }
          }]
        })
      },

      renameFile(fileId, documentIndex, fileIndex) {
        this.$modal.show('rename-modal', {
          fileName      : this.documents[documentIndex].files[fileIndex].file_name,
          fileId        : fileId,
          documentIndex : documentIndex,
          fileIndex     : fileIndex,
          renameFileDone: this.renameFileDone
        })
      },

      renameFileDone(documentIndex, fileIndex, fileName) {
        this.documents[documentIndex].files[fileIndex].file_name = fileName
        EventBus.$emit('notification-event', {
          'type'   : 'success',
          'message': "File renamed successfully"
        })
      },

      deleteFile(fileId, documentIndex, fileIndex) {
        this.$modal.show('confirm-modal', {
          // width : 600,
          title  : "Please confirm your action",
          message: "Are you sure you want to delete this?",
          buttons:[{
            label          : 'Yes',
            color          : 'red',
            loadingViaLocal: true, // a bit of HACK, we want to show loading image when button is clicked, but hard to pass loading prop to a modal
            action         : () => {
              this.apiDelete({
                clientId: this.$route.params.clientId,
                fileId  : fileId
              }, 'document/delete-file', () => {
                this.documents[documentIndex].files.splice(fileIndex, 1)
                EventBus.$emit('notification-event', {
                  'type'   : 'success',
                  'message': "File deleted successfully"
                })
                this.$modal.hide('confirm-modal')
              })
            }
          }, {
            label : 'No',
            color : 'gray',
            action: () => {
              this.$modal.hide('confirm-modal')
            }
          }]
        })
      },
    },

    metaInfo () {
      return {
        title: this.getClientPrimaryApplicant != null ? this.getApplicantName(this.getClientPrimaryApplicant) : "Brokerpad"
      }
    }
  }
</script>